import {
    ChangeDetectorRef,
    Component,
    DestroyRef,
    inject,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {BehaviorSubject, finalize, of} from 'rxjs';
import {attributesFilterFieldMap as fieldMap} from '@emm/shared/formly/maps/attributes-filter.config';
import {filter, map, switchMap, take} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
    ManagementAttributesFormComponent
} from '@emm/management/components/management-attributes/management-attributes-form/management-attributes-form.component';
import {defaultContextMenuAction} from '@emm/core/models/context-menu-action.model';
import {AttributesFacadeService} from '@emm/management/services/attributes-facade.service';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {AddAttributeDialogComponent} from '@emm/shared/dialogs/add-attribute-dialog/add-attribute-dialog.component';
import {SearchFilter} from '@emm/core/models/search-filter';
import { Attribute } from '@emm/core/models/attribute.model';
import {UserService} from '@emm/core/services/user.service';
import {API_ERRORS} from '@emm/core/util/api-constants';


@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-management-attributes',
    styleUrls: ['./management-attributes.component.scss'],
    templateUrl: './management-attributes.component.html'
})
export class ManagementAttributesComponent implements OnInit {

    @ViewChild(ManagementAttributesFormComponent) attributesForm: ManagementAttributesFormComponent;

    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    attributes$ = this.attributesFacade.attributes$;
    attributesContextMenuActions = defaultContextMenuAction;
    centerLoading$ = this.attributesFacade.centerLoading$;
    fields$ = of(Object.keys(fieldMap).reduce((acc, cur) => [...acc, fieldMap[cur]], []));
    isReadOnly$ = this.authFacade.isReadOnly$;
    isReadOnly = this.userService.hasLoginRole();
    isSuperAdmin = this.userService.hasSuperAdminRole();
    isAdmin = this.userService.hasAdminRole();
    isAuthor = this.userService.hasAuthorRole();
    rightLoading$ = this.attributesFacade.rightLoading$;
    anyLoading$ = this.attributesFacade.anyLoading$;
    selectedAttribute$ = this.attributesFacade.attributeDetail$;
    attributesCache = [];
    attributeTypes;
    attributesAsGenericItems$ = this.attributesFacade.attributesAsGenericItems$;
    addClicked$ = this.attributesFacade.onAddClicked();
    disableButtons = new BehaviorSubject(true);
    showButtons = new BehaviorSubject<boolean>(false);

    constructor(
        private attributesFacade: AttributesFacadeService,
        private authFacade: AuthFacadeService,
        private dialog: MatDialog,
        private notificationService: NotificationService,
        private userService: UserService,
        private changeDetector: ChangeDetectorRef,
    ) {
    }


    ngOnInit(): void {
        this.attributesFacade.init();
        this.attributes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.anyLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.getAttributes();
        this.attributesFacade.attributeTypes$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(result => this.attributeTypes = result);
        this.selectedAttribute$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.disableButtons.next(this.attributesForm?.form?.invalid || this.attributesForm?.form?.pristine);
        this.showButtons.next(this.attributesForm?.form?.pristine);
    }

    getAttributes(): void {
        this.attributesFacade.startProgressBar();
        this.attributesFacade.api.getAllAttributes()
            .pipe(
                finalize(() => this.attributesFacade.stopProgressBar()),
                takeUntilDestroyed(this.destroyRef))
            .subscribe(result => {
            this.attributesCache = result;
            this.attributes$.next(result);
        });
    }

    openCreateItemForm(): void {
        this.attributesFacade.nextAddClicked(true);
    }

    openCreateAttributeDialog(): void {
        const attributeTypes = this.attributeTypes;
        const dialogRef = this.dialog.open(AddAttributeDialogComponent, {
            data: {createAttribute: true, attributeTypes},
            width: '600px'
        });

        dialogRef.afterClosed()
            .pipe(
                filter((attribute) => attribute),
                switchMap((attribute) => this.attributesFacade.api.createAttribute(attribute)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.centerLoading$ = of(true);
                    this.notificationService.success('Created successfully');
                    this.getAttributes();
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }


    onFilter(searchFilter: SearchFilter): void {
        this.filterNodes(searchFilter.value as string, searchFilter.key)
    }


    filterNodes(value: string, selected: string): void {
        this.attributes$.next(this.attributesCache);
        this.attributes$.pipe(
            take(1),
            map(list => {
                let filteredNodes;
                try {
                    switch (selected) {
                        case 'locked':
                            filteredNodes = list.filter(nd => nd['locked'].toString() === value.toString());
                            break;
                        case selected:
                            filteredNodes = list.filter(nd =>
                                nd[selected].toLowerCase().includes(value.toLowerCase()));
                            break;
                        default:
                            filteredNodes = list.filter(nd => nd['name'].toLowerCase().includes(value.toLowerCase()));
                            break;
                    }
                    this.attributes$.next(filteredNodes);
                } catch (e) {}
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }


    onSelectAttribute(selected: Attribute): void {
        this.changeDetector.detectChanges();
        this.attributesFacade.nextAddClicked(false);
        this.attributesFacade.selectAttribute(selected);
    }

    submit() {
        this.attributesForm?.submit();
    }

    cancel() {
        this.attributesForm?.cancel();
    }

    renderButtons(event: boolean): void {
        this.showButtons.next(event);
    }

    handleCancel(event: boolean): void {
        this.disableButtons.next(event);
    }
}
